import { useEffect, useMemo, useState, type DragEvent } from "react";
import { Trash2, UploadCloud } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DocumentTypeSelector } from "@/components/pets/DocumentTypeSelector";
import type { DocumentAttachmentUpload } from "@/hooks/useExamAttachments";
import { parseISODateOnlyLocal, toISODateOnlyLocal } from "@/lib/date-utils";
import {
  DEFAULT_DOCUMENT_TYPE_NAMES,
  DOCUMENT_FILE_ACCEPT,
  MAX_DOCUMENT_FILE_SIZE_MB,
  getDocumentTitleFromFilename,
  isAcceptedDocumentFile,
  isFileWithinDocumentSizeLimit,
  normalizeDocumentTypeName,
} from "@/lib/pet-documents/document-upload";
import { cn } from "@/lib/utils";
import { generateUUID } from "@/lib/utils/uuid";

interface PendingAttachment extends DocumentAttachmentUpload {
  id: string;
  previewUrl: string;
  name: string;
  documentTime: string;
}

interface DocumentAttachmentsUploadProps {
  clinicId?: string | null;
  onUpload: (items: DocumentAttachmentUpload[]) => Promise<void>;
}

export function DocumentAttachmentsUpload({ clinicId, onUpload }: DocumentAttachmentsUploadProps) {
  const { t } = useTranslation("medicalRecords");
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const getDefaultDocumentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  };

  const combineDocumentDateTime = (documentDate?: Date | string | null, documentTime?: string) => {
    if (!documentDate || !documentTime) return null;
    const baseDate = documentDate instanceof Date
      ? new Date(documentDate)
      : parseISODateOnlyLocal(documentDate);
    if (!baseDate) return null;

    const [hours, minutes] = documentTime.split(":");
    const parsedHours = Number.parseInt(hours ?? "", 10);
    const parsedMinutes = Number.parseInt(minutes ?? "", 10);
    if (
      Number.isNaN(parsedHours) ||
      Number.isNaN(parsedMinutes) ||
      parsedHours < 0 ||
      parsedHours > 23 ||
      parsedMinutes < 0 ||
      parsedMinutes > 59
    ) {
      return null;
    }

    baseDate.setHours(parsedHours, parsedMinutes, 0, 0);
    return baseDate;
  };

  useEffect(
    () => () => {
      pending.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    },
    [pending],
  );

  const handleFilesSelected = (files: FileList | null) => {
    if (!files?.length) return;

    const validFiles: File[] = [];
    const rejectedBySize: string[] = [];
    const rejectedByType: string[] = [];

    Array.from(files).forEach((file) => {
      if (!isFileWithinDocumentSizeLimit(file)) {
        rejectedBySize.push(file.name);
        return;
      }
      if (!isAcceptedDocumentFile(file)) {
        rejectedByType.push(file.name);
        return;
      }
      validFiles.push(file);
    });

    if (rejectedBySize.length > 0) {
      toast.error(t("attachments.fileSizeError", {
        files: rejectedBySize.join(", "),
        size: MAX_DOCUMENT_FILE_SIZE_MB,
      }));
    }

    if (rejectedByType.length > 0) {
      toast.error(t("attachments.fileTypeError", {
        files: rejectedByType.join(", "),
      }));
    }

    if (validFiles.length === 0) return;

    setPending((prev) => [
      ...prev,
      ...validFiles.map((file) => ({
        id: generateUUID(),
        file,
        name: getDocumentTitleFromFilename(file.name),
        type: "",
        category: "",
        description: "",
        documentDate: new Date(),
        documentTime: getDefaultDocumentTime(),
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      })),
    ]);
  };

  const updatePending = (id: string, patch: Partial<PendingAttachment>) => {
    setPending((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const handleDragState = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemove = (id: string) => {
    const match = pending.find((item) => item.id === id);
    if (match?.previewUrl) URL.revokeObjectURL(match.previewUrl);
    setPending((prev) => prev.filter((item) => item.id !== id));
  };

  const disabledMessage = useMemo(() => {
    if (!clinicId) return t("attachments.disabledNoClinic");
    if (!pending.length) return null;
    if (pending.some((item) => !item.name.trim())) return t("attachments.disabledMissingTitle");
    if (
      pending.some(
        (item) =>
          !DEFAULT_DOCUMENT_TYPE_NAMES.includes(
            item.type.trim() as (typeof DEFAULT_DOCUMENT_TYPE_NAMES)[number],
          ),
      )
    ) {
      return t("attachments.disabledMissingType");
    }
    if (pending.some((item) => !item.documentDate)) return t("attachments.disabledMissingDate");
    if (pending.some((item) => !combineDocumentDateTime(item.documentDate, item.documentTime))) {
      return t("attachments.disabledMissingTime");
    }
    return null;
  }, [clinicId, pending, t]);

  const handleUploadClick = async () => {
    if (!pending.length || !clinicId || disabledMessage) return;

    setIsUploading(true);
    try {
      await onUpload(
        pending.map((item) => {
          const documentType = normalizeDocumentTypeName(item.type);
          const documentDateTime = combineDocumentDateTime(item.documentDate, item.documentTime);
          return {
            file: item.file,
            name: item.name.trim(),
            type: documentType,
            category: documentType,
            description: item.description?.trim() || null,
            documentDate: documentDateTime ?? item.documentDate,
          };
        }),
      );
      pending.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      setPending([]);
    } finally {
      setIsUploading(false);
    }
  };

  const renderDropzone = (compact = false) => (
    <label
      className={cn(
        "flex cursor-pointer items-center justify-center rounded-md border border-dashed text-center text-sm text-muted-foreground transition-colors",
        compact ? "px-4 py-3" : "flex-col p-6",
        isDragging && "border-primary bg-primary/5 text-foreground",
      )}
      onDragEnter={(event) => {
        handleDragState(event);
        setIsDragging(true);
      }}
      onDragOver={handleDragState}
      onDragLeave={(event) => {
        handleDragState(event);
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsDragging(false);
        }
      }}
      onDrop={(event) => {
        handleDragState(event);
        setIsDragging(false);
        handleFilesSelected(event.dataTransfer.files);
      }}
    >
      <input
        type="file"
        multiple
        accept={DOCUMENT_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => handleFilesSelected(event.target.files)}
      />
      <UploadCloud className={cn("h-5 w-5", compact ? "mr-2" : "mb-2")} />
      {t("attachments.empty")}
      {!compact ? (
        <>
          <p className="mt-1 text-xs">{t("attachments.acceptedFormats")}</p>
          <p className="mt-1 text-xs font-medium">
            {t("attachments.maxSize", { size: MAX_DOCUMENT_FILE_SIZE_MB })}
          </p>
        </>
      ) : null}
    </label>
  );

  return (
    <div className="flex min-h-0 flex-col bg-background">
      <div className="flex shrink-0 flex-col items-start gap-3 border-b px-1 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold">{t("attachments.reviewTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("attachments.reviewDescription")}</p>
        </div>
        <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
          <label className="flex w-full cursor-pointer items-center justify-center gap-2 sm:w-auto">
            <UploadCloud className="h-4 w-4" />
            {t("attachments.select")}
            <input
              type="file"
              multiple
              accept={DOCUMENT_FILE_ACCEPT}
              className="hidden"
              onChange={(event) => handleFilesSelected(event.target.files)}
            />
          </label>
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-4 pr-2">
        {pending.length === 0 ? renderDropzone() : (
          <div className="space-y-4">
            <div className="hidden sm:flex flex-col items-center gap-4">
              <div>
                {renderDropzone(true)}
              </div>
              <p className="-mt-2 text-center text-xs text-muted-foreground">
                {t("attachments.maxSize", { size: MAX_DOCUMENT_FILE_SIZE_MB })}
              </p>
            </div>
            {pending.map((item, index) => (
              <div key={item.id} className="space-y-3 rounded-lg border p-4">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive self-end"
                    onClick={() => handleRemove(item.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span className="hidden sm:block">{t("attachments.remove")}</span>
                  </Button>
                </div>
                <div className="flex flex-col items-start justify-between gap-3">
                    <p className="text-xs font-medium uppercase text-muted-foreground">
                      {t("attachments.documentCardTitle", { index: index + 1 })}
                    </p>
                    <p className="text-sm font-medium">{item.file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>
                      {t("attachments.documentName")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder={t("attachments.documentNamePlaceholder")}
                      value={item.name}
                      onChange={(event) => updatePending(item.id, { name: event.target.value })}
                      required
                    />
                  </div>

                  <DocumentTypeSelector
                    value={item.type}
                    onChange={(type) => updatePending(item.id, { type, category: type })}
                  />

                  <div className="space-y-2">
                    <Label>{t("attachments.documentDate")}</Label>
                    <Input
                      type="date"
                      value={item.documentDate ? toISODateOnlyLocal(new Date(item.documentDate)) : ""}
                      onChange={(event) =>
                        updatePending(item.id, {
                          documentDate: event.target.value
                            ? parseISODateOnlyLocal(event.target.value) ?? new Date()
                            : null,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t("attachments.documentTime")}</Label>
                    <Input
                      type="time"
                      value={item.documentTime}
                      onChange={(event) => updatePending(item.id, { documentTime: event.target.value })}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label>{t("attachments.note")}</Label>
                    <Textarea
                      placeholder={t("attachments.notePlaceholder")}
                      value={item.description ?? ""}
                      onChange={(event) => updatePending(item.id, { description: event.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          className="w-full"
          onClick={handleUploadClick}
          disabled={isUploading || !pending.length || !clinicId || Boolean(disabledMessage)}
        >
          {isUploading ? (
            t("attachments.uploading")
          ) : (
            <>
              <UploadCloud className="mr-2 h-4 w-4" />
              {t("attachments.upload")}
            </>
          )}
        </Button>
        {disabledMessage ? (
          <p className="text-center text-xs text-muted-foreground">{disabledMessage}</p>
        ) : null}
      </div>
    </div>
  );
}
